import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { useCategoriesQuery } from "@framework/categories/categories-menu.query";
import { CategoryMenu } from "@framework/types";
import cn from "classnames";
import Image from '@components/ui/image';
import dynamic from 'next/dynamic';
import { isMobileOnly } from "react-device-detect";
import { useTranslation } from "next-i18next";
import Input from '@components/ui/forms/input';
import _ from "lodash";
import { downcaseAndRemoveSpaces } from "@lib/string";

const IndustriesLoader = dynamic(() => import("@components/ui/loaders/industries-loader"));
const ErrorMessage = dynamic(() => import("@components/ui/error-message"));
const CategoriesArrowParentIcon = dynamic(() => import("@components/icons/new/categories-arrow-parent-icon"));
const CategoriesArrowChildIcon = dynamic(() => import("@components/icons/new/categories-arrow-child-icon"));
const ChildrenItem = dynamic(() => import("@components/layouts/menu/templates/new-categories/children"));
const SearchIcon = dynamic(() => import('@components/icons/new/search-icon'));
const ClearInputIcon = dynamic(() => import('@components/icons/new/clear-input-icon'));

interface NewCategoriesTemplateProps {
  onClickCategoryItemHandler: (item: CategoryMenu) => void;
  activeLoadData: boolean;
}
const NewCategoriesTemplate: React.FC<NewCategoriesTemplateProps> = ({
  onClickCategoryItemHandler,
  activeLoadData
}) => {
  const router = useRouter();
  const locale = router.locale;
  const {
    isLoading,
    isError,
    error,
    data,
  } = useCategoriesQuery({ locale }, {
    staleTime: Infinity,
    enabled: activeLoadData
  });
  const [categoryName, setCategoryName] =  useState<string>("");;
  const { t } = useTranslation('common');
  const [activeItem, setActiveItem] =  useState<CategoryMenu | any>(null);
  const [selectCategories, setSelectCategories] = useState(data?.categories);

  var debounce_fun = _.debounce(function (e) {
    const value = (e.target.value || "").trim();

    if(value != categoryName) setCategoryName(value);
  }, 1000);

  const {
    isLoading: childLoading,
    // isError,
    // error,
    data: childItems,
  } = useCategoriesQuery({ locale, parentId: activeItem?.id }, {
    staleTime: Infinity,
    enabled: activeItem?.id != null
  });

  useEffect(() => {
    if(childItems?.categories?.length == 0) {
      onClickCategoryItemHandler(activeItem);
    }
  }, [childItems?.categories])

  useEffect(() => {
    if(!activeLoadData) {
      setActiveItem(null);
    }
  }, [activeLoadData])

  useEffect(() => {
    if(!categoryName) {
      setSelectCategories(data?.categories);
      return;
    }

    const newSelectIndustries = data?.categories.filter((item: any) => {
      return downcaseAndRemoveSpaces(item.name).includes(downcaseAndRemoveSpaces(categoryName))
    });

    setSelectCategories(newSelectIndustries)
  }, [categoryName, data?.categories])

  if (isError && error) return <ErrorMessage message={error.message} />;

  return (
    <>
      <div className="px-4 py-3">
        <div className='relative'>
          <span className='absolute left-4 top-2'>
            <SearchIcon width="24" height="24" color="#818898" />
          </span>
          <Input
            name="search-shop"
            title=''
            key={categoryName}
            defaultValue={categoryName}
            inputClassName={cn('h-[40px] bg-gray-1800 rounded-[20px] pl-12 text-gray-1500 text-sm be-viet-pro', (categoryName || "")?.length > 0 && "pr-[42px]")}
            placeholder={t("common:text-search-page")}
            onInput={(e: any) => debounce_fun(e)}
          />

          {(categoryName || "")?.length > 0 && <span className='absolute right-3 top-2 cursor-pointer' onClick={() => setCategoryName("")}>
            <ClearInputIcon />
          </span>}
        </div>
      </div>
      <div id="tabs-menu-category" className="h-category-new">
        {!isLoading && selectCategories?.map((parent: any, index: number) => {
          const pattern = new RegExp(categoryName, 'gi');
          return <div key={parent.id} className="nav-item hover:bg-accent/[0.28] hover:text-accent be-viet-pro" onClick={() => setActiveItem(parent)}>
            <a className="nav-link cursor-pointer" onClick={(event) => {
              event.stopPropagation();
              setActiveItem(parent)
            }}>
              {/* {parent?.icon && (
                <Image
                priority
                src={`${process.env.URL_S3}/${parent.icon}`}
                alt={"name"}
                layout="fixed"
                width={32}
                height={32}
                className="max-h-full max-w-full" />
              )} */}
              <span className="capitalize" dangerouslySetInnerHTML={{__html: parent?.name?.replace(pattern, '<span class="highlight">$&</span>')}}></span>
            </a>

            {parent.count_products > 0 && (
              <a className="btn--show-hide-categories cursor-pointer">
                <CategoriesArrowParentIcon />
              </a>
            )}
          </div>
        })}

        {
          isLoading && Array(18).fill(0).map((_item: any, index: number) => {
            return <div key={index} className="nav-item">
              <IndustriesLoader />
            </div>
          })
        }
      </div>

      {
        !childLoading && activeItem && childItems?.categories?.length > 0 && <div id="content-menu-category-pc" key={activeItem?.id} className="tab-active tab-content">
          <div className="content-submenu-dm-pc">
            <div className="parent-header">
              <a onClick={() => setActiveItem(null)} className="cursor-pointer">
                <CategoriesArrowChildIcon />
              </a>

              <span onClick={() => onClickCategoryItemHandler(activeItem)} className="cursor-pointer hover:text-accent">{activeItem.name}</span>
            </div>

            <div className={isMobileOnly ? "row-child-dm--pc row-child-dm--pc-mobile" : "row-child-dm--pc"}>
              <ul>
                {childItems?.categories?.map((item: any) => (
                  <ChildrenItem onClickCategoryItemHandler={onClickCategoryItemHandler} key={item.id} data={item}/>
                ))}
              </ul>
            </div>
          </div>
        </div>
      }
    </>
  );
};

export default NewCategoriesTemplate;
